import { toCamelCase } from "@estaly/ui";
import StoresService from "@/api/services/stores";

const initialState = {
  isLoading: false,
  index: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  selectedStoreId: "",
  currentStore: null,
  selectedFilters: {},
};

export default {
  namespaced: true,

  state: initialState,

  getters: {
    selectedStoreId: (state) => {
      return state.selectedStoreId;
    },

    selectedStore: (state) => {
      return state.index.find((store) => store.id === state.selectedStoreId);
    },

    isLoading: (state) => {
      return state.isLoading;
    },

    stores: (state) => {
      return state.index;
    },

    hasMultipleStores: (state) => {
      return state.index?.length > 1;
    },

    recordASalePermission: (state, getters) => {
      return getters.selectedStore?.storeFeatureConfiguration?.recordASale;
    },

    hasPlanSaleUpdatePermission: (state) => (storeId) => {
      const store = state.index.find((s) => s.id === storeId);
      return store?.storeFeatureConfiguration?.planSaleUpdatePermissions ?? false;
    },

    storeRetributionPaymentMethodEstalyGiftCard:
      (state) =>
      (store = state.currentStore) => {
        return store?.retributionsPayoutMethod === "estaly_gift_card";
      },

    retributionsBoostEnabled:
      (state) =>
      (store = state.currentStore) => {
        return store?.retributionsBoostEnabled;
      },

    storeOffline: (state) => {
      return state.currentStore?.source === "offline";
    },

    activeStores: (state) => {
      return state.index.filter((store) => store.status === "active");
    },

    activeOfflineStores: (state) => {
      return state.index.filter((store) => store.source === "offline" && store.status === "active");
    },

    isStoreActive: () => (store) => {
      return store?.status === "active";
    },

    isStoreSetup: () => (store) => {
      return store?.status === "setup";
    },

    isOfflineStore: () => (store) => {
      return store?.source === "offline";
    },

    isStoreInvoiceLevel: () => (store) => {
      return store?.organization?.invoiceLevel === "store";
    },

    isBillingEntityLevel: () => (store) => {
      return store?.organization?.invoiceLevel === "billing_entity";
    },

    canEditInvoicing: (state, getters) => (store) => {
      if (getters.isStoreInvoiceLevel(store)) return true;
      if (getters.isBillingEntityLevel(store)) return !store?.billingEntity;
      return false;
    },

    billingBusinessName: (state, getters) => (store) => {
      if (getters.isStoreInvoiceLevel(store)) return store?.businessName;
      if (getters.isBillingEntityLevel(store))
        return store?.billingEntity ? store.billingEntity.businessName : store?.businessName;
      return store?.organization?.businessName;
    },

    billingAddress: (state, getters) => (store) => {
      if (getters.isStoreInvoiceLevel(store)) return store?.billingAddress;
      if (getters.isBillingEntityLevel(store))
        return store?.billingEntity ? store.billingEntity.billingAddress : store?.billingAddress;
      return store?.organization?.billingAddress;
    },

    getStatuses: () => () => {
      return ["active", "information_to_be_completed", "pending_verification"];
    },

    getIbanStatuses: () => () => {
      return ["missing", "filed"];
    },

    getStoreStatusFieldDisplayStyle: () => (status) => {
      switch (status) {
        case "active":
          return "bg-green-100 text-green-800";
        case "inactive":
          return "bg-red-100 text-red-800";
        case "pending_verification":
          return "bg-blue-100 text-blue-800";
        case "information_to_be_completed":
          return "bg-yellow-100 text-yellow-800";
      }
    },

    getRevenueRanges: () => () => {
      return ["0_to_250k", "250k_to_1M", "1M_to_5M", "5M_to_10M", "above_10M"];
    },

    getOwnershipTypes: () => () => {
      return ["branch", "association", "franchisee", "independant"];
    },

    getOwnershipTypeBadgeClass: () => (ownershipType) => {
      switch (ownershipType) {
        case "branch":
          return "bg-blue-100 text-blue-700";
        case "association":
          return "bg-purple-100 text-purple-700";
        case "franchisee":
          return "bg-yellow-100 text-yellow-700";
        case "independant":
          return "bg-green-100 text-green-700";
        default:
          return "bg-gray-100 text-gray-600";
      }
    },
  },

  mutations: {
    setCurrentStore(state, store) {
      state.currentStore = store;
    },

    setIndex(state, data) {
      state.index = data.records;
      state.currentPage = data.page.currentPage || 1;
      state.totalPages = data.page.totalPages || 0;
      state.totalCount = data.page.totalCount || 0;
    },

    setIsLoading(state, val) {
      state.isLoading = val;
    },

    resetFilters(state) {
      for (const filter of Object.keys(state.selectedFilters)) {
        state.selectedFilters[filter] = [];
      }
    },

    setSelectedStoreId(state, storeId) {
      state.selectedStoreId = storeId;
    },

    resetState(state) {
      state.isLoading = initialState.isLoading;
      state.selectedStoreId = initialState.selectedStoreId;
    },
  },

  actions: {
    updateStoreId({ commit }, storeId) {
      commit("setSelectedStoreId", storeId);
    },

    async create(/* eslint-disable-line no-unused-vars */ _) {
      return StoresService.create();
    },

    async fetchActiveStores({ dispatch }, { page, perPage }) {
      return await dispatch("fetchIndex", {
        status: ["active"],
        includes: ["store_feature_configuration"],
        page,
        perPage,
      });
    },

    async fetchIndex(
      { commit },
      { status = [], page = null, perPage = null, keywords = null, iban = [], includes = [], region = [] },
    ) {
      const response = await StoresService.fetchIndex({ page, perPage, keywords, status, iban, includes, region });

      if (!response?.success) return response;

      const data = toCamelCase(response.data);
      commit("setIndex", data);

      return response;
    },

    async fetch({ commit, dispatch, state }, { id = state.selectedStoreId, includes = [] } = {}) {
      const params = {};
      if (includes.length > 0) params.includes = includes;

      const response = await StoresService.fetch(id, params);
      if (!response?.success) {
        dispatch("resetCurrentStore");
        return response;
      }

      const store = toCamelCase(response.data);
      commit("setCurrentStore", store);
      return response;
    },

    async updateStoreStatus(_, { storeId, status }) {
      return StoresService.updateStatus(storeId, status);
    },

    resetCurrentStore({ commit }) {
      commit("setCurrentStore", null);
    },

    withLoader({ commit }, action) {
      commit("setIsLoading", true);
      return action().finally(() => {
        commit("setIsLoading", false);
      });
    },
  },
};
